import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Table, Spin, message } from 'antd';
import {
  HomeOutlined,
  UserOutlined,
  FileTextOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import DashboardFilter from './components/DashboardFilter';
import CompanyDetailModal from './components/CompanyDetailModal';
import {
  getDashboardStats,
  getCompanyStats,
  getCandidateStats,
  getPositionStats,
  getCompanyDetail,
} from 'api/dashboard';
import HomeWrapper from 'pages/Dashboard/styles';

const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [stats, setStats] = useState({
    companies: { total: 0 },
    candidates: { total: 0, careerRequests: 0 },
    positions: { total: 0, active: 0 },
  });
  const [companyStats, setCompanyStats] = useState([]);
  const [candidateStats, setCandidateStats] = useState([]);
  const [positionStats, setPositionStats] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetailVisible, setCompanyDetailVisible] = useState(false);
  const [companyDetailLoading, setCompanyDetailLoading] = useState(false);
  const [companyDetailData, setCompanyDetailData] = useState(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load overall statistics
      const statsData = await getDashboardStats(filters);
      if (statsData) {
        setStats({
          companies: statsData.companies || { total: 0 },
          candidates: statsData.candidates || { total: 0, careerRequests: 0 },
          positions: statsData.positions || { total: 0, active: 0 },
        });
      }

      // Load company statistics
      const companiesData = await getCompanyStats(filters);
      if (companiesData && Array.isArray(companiesData)) {
        setCompanyStats(companiesData);
      }

      // Load candidate statistics
      const candidatesData = await getCandidateStats(filters);
      if (candidatesData && Array.isArray(candidatesData)) {
        setCandidateStats(candidatesData);
      }

      // Load position statistics
      const positionsData = await getPositionStats(filters);
      if (positionsData && Array.isArray(positionsData)) {
        setPositionStats(positionsData);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      message.error(t('dashboard.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleCompanyClick = async (companyId) => {
    try {
      setCompanyDetailLoading(true);
      setCompanyDetailVisible(true);
      const detail = await getCompanyDetail(companyId, filters);
      setCompanyDetailData(detail);
      setSelectedCompany(companyId);
    } catch (error) {
      console.error('Error loading company detail:', error);
      message.error(t('dashboard.error.loadCompanyDetailFailed'));
    } finally {
      setCompanyDetailLoading(false);
    }
  };

  const companyColumns = [
    {
      title: t('companies.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => handleCompanyClick(record.id)}>{text}</a>
      ),
    },
    {
      title: t('dashboard.companyStats.jobsCount'),
      dataIndex: 'jobsCount',
      key: 'jobsCount',
      align: 'center',
    },
    {
      title: t('dashboard.companyStats.applicationsCount'),
      dataIndex: 'applicationsCount',
      key: 'applicationsCount',
      align: 'center',
    },
  ];

  const candidateColumns = [
    {
      title: t('companies.name'),
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t('dashboard.candidateStats.applicationsCount'),
      dataIndex: 'applicationsCount',
      key: 'applicationsCount',
      align: 'center',
    },
  ];

  const positionColumns = [
    {
      title: t('companies.name'),
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t('dashboard.positionStats.positionsCount'),
      dataIndex: 'positionsCount',
      key: 'positionsCount',
      align: 'center',
    },
  ];

  return (
    <HomeWrapper>
      <div className="viewSummary">
        <DashboardFilter onFilterChange={handleFilterChange} initialFilters={filters} />
        
        <Spin spinning={loading}>
          <Row gutter={[16, 16]} className="row-chart">
            {/* Company Statistics Card */}
            <Col xs={24} sm={12} lg={6} className="col-block">
              <Card>
                <Statistic
                  title={t('dashboard.stats.companies.total')}
                  value={stats.companies.total}
                  prefix={<HomeOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>

            {/* Candidate Statistics Card */}
            <Col xs={24} sm={12} lg={6} className="col-block">
              <Card>
                <Statistic
                  title={t('dashboard.stats.candidates.total')}
                  value={stats.candidates.total}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>

            {/* Career Requests Card */}
            <Col xs={24} sm={12} lg={6} className="col-block">
              <Card>
                <Statistic
                  title={t('dashboard.stats.candidates.careerRequests')}
                  value={stats.candidates.careerRequests}
                  prefix={<AppstoreOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>

            {/* Position Statistics Card */}
            <Col xs={24} sm={12} lg={6} className="col-block">
              <Card>
                <Statistic
                  title={t('dashboard.stats.positions.total')}
                  value={stats.positions.total}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
                <div style={{ marginTop: 8, fontSize: 14 }}>
                  {t('dashboard.stats.positions.active')}: {stats.positions.active}
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {/* Company Statistics Table */}
            <Col xs={24} lg={12}>
              <Card title={t('dashboard.tables.companies')}>
                <Table
                  columns={companyColumns}
                  dataSource={companyStats}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>

            {/* Candidate Statistics Table */}
            <Col xs={24} lg={12}>
              <Card title={t('dashboard.tables.candidates')}>
                <Table
                  columns={candidateColumns}
                  dataSource={candidateStats}
                  loading={loading}
                  rowKey="companyId"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {/* Position Statistics Table */}
            <Col xs={24}>
              <Card title={t('dashboard.tables.positions')}>
                <Table
                  columns={positionColumns}
                  dataSource={positionStats}
                  loading={loading}
                  rowKey="companyId"
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </Spin>

        <CompanyDetailModal
          visible={companyDetailVisible}
          onClose={() => {
            setCompanyDetailVisible(false);
            setCompanyDetailData(null);
            setSelectedCompany(null);
          }}
          companyData={companyDetailData}
          loading={companyDetailLoading}
        />
      </div>
    </HomeWrapper>
  );
};

export default Dashboard;
